import { midiToFrequency, midiToPlaybackRate } from "./midiConstants";

/**
 * Play a note using 2-operator FM synthesis (electric piano timbre).
 * Works with both AudioContext and OfflineAudioContext.
 * Returns the nodes for later cleanup.
 */
export function playFMNote(ctx, destination, midi, when, durationSec, velocity = 100) {
  const freq = midiToFrequency(midi);
  const vol = (velocity / 127) * 0.3;
  const end = when + durationSec;

  // Modulator: sine at 2× carrier freq
  const mod = ctx.createOscillator();
  const modGain = ctx.createGain();
  mod.type = "sine";
  mod.frequency.setValueAtTime(freq * 2, when);
  modGain.gain.setValueAtTime(freq * 1.5, when);
  modGain.gain.exponentialRampToValueAtTime(Math.max(freq * 0.1, 0.001), end);

  // Carrier: sine at fundamental freq
  const carrier = ctx.createOscillator();
  const carrierGain = ctx.createGain();
  carrier.type = "sine";
  carrier.frequency.setValueAtTime(freq, when);

  // Amplitude envelope
  carrierGain.gain.setValueAtTime(0.001, when);
  carrierGain.gain.linearRampToValueAtTime(vol, when + 0.005);
  if (durationSec > 0.06) {
    carrierGain.gain.exponentialRampToValueAtTime(Math.max(vol * 0.6, 0.001), when + 0.05);
    carrierGain.gain.setValueAtTime(Math.max(vol * 0.6, 0.001), end - 0.05);
  }
  carrierGain.gain.exponentialRampToValueAtTime(0.001, end);

  mod.connect(modGain);
  modGain.connect(carrier.frequency);
  carrier.connect(carrierGain);
  carrierGain.connect(destination);

  mod.start(when);
  carrier.start(when);
  mod.stop(end + 0.1);
  carrier.stop(end + 0.1);

  return { mod, carrier, carrierGain };
}

/**
 * Play a note using a pitch-shifted sample (recorded at middle C / MIDI 60).
 */
export function playSampleNote(ctx, destination, sampleBuffer, midi, when, durationSec, velocity = 100) {
  const source = ctx.createBufferSource();
  source.buffer = sampleBuffer;
  source.playbackRate.value = midiToPlaybackRate(midi);

  const gainNode = ctx.createGain();
  const vol = (velocity / 127) * 0.5;
  const end = when + durationSec;

  gainNode.gain.setValueAtTime(vol, when);
  if (durationSec > 0.03) {
    gainNode.gain.setValueAtTime(vol, end - 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.001, end);
  }

  source.connect(gainNode);
  gainNode.connect(destination);
  source.start(when);
  source.stop(end + 0.1);

  return source;
}

/**
 * Play a note using a per-note soundfont buffer (no pitch-shifting needed).
 * `buffers` is a { [midi]: AudioBuffer } map from soundfontLoader.
 * Falls back to FM synthesis if the note buffer is missing.
 */
export function playSoundfontNote(ctx, destination, buffers, midi, when, durationSec, velocity = 100) {
  const buffer = buffers[midi];
  if (!buffer) {
    return playFMNote(ctx, destination, midi, when, durationSec, velocity);
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const gainNode = ctx.createGain();
  const vol = (velocity / 127) * 0.5;
  const end = when + durationSec;

  gainNode.gain.setValueAtTime(vol, when);
  // Let the natural sample play; fade out at note end
  if (durationSec > 0.05) {
    gainNode.gain.setValueAtTime(vol, end - 0.04);
    gainNode.gain.exponentialRampToValueAtTime(0.001, end);
  }

  source.connect(gainNode);
  gainNode.connect(destination);
  source.start(when);
  source.stop(end + 0.1);

  return source;
}
